import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Search,
  Layers,
  Printer,
  Package,
  Calendar,
  Building2,
  Zap,
  Check,
  Sparkles,
  FileText,
  SlidersHorizontal,
  X,
  Boxes,
} from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { useQuery } from "@tanstack/react-query";
import { cn, formatNumber } from "@/lib/utils";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";

export interface PrepressPoolGroup {
  id: string;
  groupKey: string;
  prepressCode: string | null;
  isPrepressAssigned: boolean;
  paperSize: string | null;
  prepressNotes: string | null;
  customerId: string;
  customerName: string;
  customerCode: string;
  primaryOrderId: string;
  primaryOrderCode: string;
  deliveryDeadline: string | null;
  layoutImage?: string | null;
  productImage?: string | null;
  items: Array<{
    id: string;
    detailId: string;
    orderId: string;
    orderCode: string;
    customerId: string;
    customerName: string;
    productId: string;
    productCode: string;
    productName: string;
    unit: string;
    quantity: number;
    plannedQty: number;
    remainingPlanQty: number;
    prepressUps: number;
    prepressPaperSize: string | null;
    prepressPrintSheets: number;
    deliveryDeadline: string | null;
    printLayoutId?: string | null;
    printLayout?: any;
    layoutImage?: string | null;
    productImage?: string | null;
  }>;
  totalUps: number;
  totalRemainingQty: number;
  totalOrderQty: number;
  calculatedRunSheets: number;
  isGangRun: boolean;
  itemCount: number;
}

interface SelectPrepressItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (group: PrepressPoolGroup) => void;
  onManualEntry?: () => void;
  currentSelectedCode?: string | null;
}

export function SelectPrepressItemModal({
  open,
  onOpenChange,
  onSelect,
  onManualEntry,
  currentSelectedCode,
}: SelectPrepressItemModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMode, setFilterMode] = useState<"ALL" | "SINGLE" | "GANG_RUN">("ALL");

  const { data, isLoading } = useQuery({
    queryKey: ["prepress-pool-modal", searchTerm],
    queryFn: async () => {
      const res = await trpcClient.productionOrders.getPrepressPool.query({
        search: searchTerm.trim() || undefined,
        hasPrepressOnly: true,
      });
      return res as {
        totalGroups: number;
        totalItems: number;
        groups: PrepressPoolGroup[];
      };
    },
    enabled: open,
    staleTime: 10000,
  });

  const allGroups = data?.groups || [];

  const filteredGroups = useMemo(() => {
    return allGroups.filter((g) => {
      if (filterMode === "SINGLE" && g.isGangRun) return false;
      if (filterMode === "GANG_RUN" && !g.isGangRun) return false;
      return true;
    });
  }, [allGroups, filterMode]);

  const singleCount = useMemo(
    () => allGroups.filter((g) => !g.isGangRun).length,
    [allGroups]
  );
  const gangCount = useMemo(
    () => allGroups.filter((g) => g.isGangRun).length,
    [allGroups]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden bg-slate-50/70 backdrop-blur-md border-slate-200">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-700 shrink-0">
                <Layers className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="flex items-center gap-2">
                  Chọn Sản Phẩm / Bài Bình Đã Bình File
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                    {allGroups.length} bài sẵn sàng
                  </Badge>
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500 mt-0.5">
                  Lựa chọn bài bình kỹ thuật đã duyệt để tự động nạp thông số khuôn kẽm, khổ in máy và công đoạn sản xuất.
                </DialogDescription>
              </div>
            </div>
          </div>

          {/* Search bar & filter pills */}
          <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Tìm theo mã bài bình, tên sản phẩm, mã đơn hàng, tên khách hàng..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9 text-xs bg-slate-50 border-slate-200 focus:bg-white"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-1.5 shrink-0 bg-slate-100 p-1 rounded-md border border-slate-200 text-xs">
              <button
                type="button"
                onClick={() => setFilterMode("ALL")}
                className={cn(
                  "px-2.5 py-1 rounded text-xs font-medium transition-colors",
                  filterMode === "ALL"
                    ? "bg-white text-indigo-700 shadow-2xs font-semibold"
                    : "text-slate-600 hover:text-slate-900"
                )}
              >
                Tất cả ({allGroups.length})
              </button>
              <button
                type="button"
                onClick={() => setFilterMode("SINGLE")}
                className={cn(
                  "px-2.5 py-1 rounded text-xs font-medium transition-colors",
                  filterMode === "SINGLE"
                    ? "bg-white text-indigo-700 shadow-2xs font-semibold"
                    : "text-slate-600 hover:text-slate-900"
                )}
              >
                Bài đơn ({singleCount})
              </button>
              <button
                type="button"
                onClick={() => setFilterMode("GANG_RUN")}
                className={cn(
                  "px-2.5 py-1 rounded text-xs font-medium transition-colors",
                  filterMode === "GANG_RUN"
                    ? "bg-white text-indigo-700 shadow-2xs font-semibold"
                    : "text-slate-600 hover:text-slate-900"
                )}
              >
                Ghép bài ({gangCount})
              </button>
            </div>
          </div>
        </DialogHeader>

        {/* List content area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3.5 max-h-[calc(90vh-175px)]">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-4 bg-white rounded-lg border border-slate-200 space-y-2">
                  <div className="flex justify-between">
                    <Skeleton className="h-5 w-40" />
                    <Skeleton className="h-5 w-24" />
                  </div>
                  <Skeleton className="h-4 w-60" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="py-12 px-4 text-center bg-white rounded-xl border border-dashed border-slate-300">
              <Boxes className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-700">
                {searchTerm ? "Không tìm thấy bài bình phù hợp" : "Chưa có bài bình nào đang chờ lập lệnh"}
              </p>
              <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                {searchTerm
                  ? "Hãy thử tìm kiếm với từ khóa khác hoặc xóa bộ lọc để xem toàn bộ danh sách."
                  : "Các chi tiết đơn hàng sau khi được Kỹ thuật Prepress bình file (ở trang Prepress Hub) sẽ tự động xuất hiện tại đây."}
              </p>
              {onManualEntry && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onOpenChange(false);
                    onManualEntry();
                  }}
                  className="mt-4 text-xs text-indigo-600 border-indigo-200"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1" /> Tự nhập thông số thủ công
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {filteredGroups.map((group) => {
                const isSelected =
                  currentSelectedCode &&
                  (group.prepressCode === currentSelectedCode ||
                    group.groupKey === currentSelectedCode);

                return (
                  <div
                    key={group.id}
                    data-testid={`prepress-group-card-${group.prepressCode || group.id}`}
                    className={cn(
                      "bg-white rounded-lg border p-4 flex flex-col justify-between gap-3 transition-all duration-200 hover:shadow-md cursor-pointer",
                      isSelected
                        ? "border-indigo-500 ring-2 ring-indigo-500/20 bg-indigo-50/20"
                        : "border-slate-200 hover:border-indigo-300"
                    )}
                    onClick={() => {
                      onSelect(group);
                      onOpenChange(false);
                    }}
                  >
                    <div>
                      {/* Top Header of Card */}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <ProductionThumbnailCell
                            imageUrl={group.productImage || group.items[0]?.productImage || null}
                            layoutUrl={group.layoutImage || group.items[0]?.layoutImage || null}
                            title={group.prepressCode || "Bài bình"}
                            type="auto"
                            size={36}
                            className="rounded-md border border-indigo-200 shadow-2xs bg-white"
                          />
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-mono font-bold text-xs text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200">
                              {group.prepressCode || "CHƯA GÁN MÃ"}
                            </span>
                            {group.isGangRun ? (
                              <Badge
                                variant="outline"
                                className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] font-semibold"
                              >
                                Ghép {group.itemCount} SP
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]"
                              >
                                Bài đơn
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="text-[11px] font-mono text-slate-500 shrink-0">
                          Đơn: <strong className="text-slate-800">{group.primaryOrderCode}</strong>
                        </div>
                      </div>

                      {/* Customer Name */}
                      <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5 break-words whitespace-normal mb-2">
                        <Building2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <span className="break-words whitespace-normal">{group.customerName}</span>
                      </div>

                      {/* 3 Metric Pills */}
                      <div className="grid grid-cols-3 gap-1.5 p-2 rounded-md bg-slate-50 border border-slate-100 text-center text-[11px] mb-2.5">
                        <div>
                          <div className="text-[10px] text-slate-400 uppercase font-medium">Khổ in</div>
                          <div className="font-mono font-bold text-slate-800 break-words whitespace-normal" title={group.paperSize || "42.5x67"}>
                            {group.paperSize || "42.5x67"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-slate-400 uppercase font-medium">Số con (UPS)</div>
                          <div className="font-mono font-bold text-indigo-600">
                            {group.totalUps} <span className="text-[9px] font-normal text-slate-400">con/tờ</span>
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-slate-400 uppercase font-medium">Tờ in chạy</div>
                          <div className="font-mono font-bold text-slate-800">
                            {formatNumber(group.calculatedRunSheets || 0)}
                          </div>
                        </div>
                      </div>

                      {/* Items list inside this prepress card */}
                      <div className="space-y-1.5">
                        <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                          Sản phẩm trong bài ({group.items.length}):
                        </div>
                        {group.items.slice(0, 3).map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between text-xs py-1 px-2 rounded bg-slate-50/70 border border-slate-100 text-slate-700 gap-2"
                          >
                            <div className="flex items-center gap-1.5 min-w-0 flex-1">
                              <ProductionThumbnailCell
                                imageUrl={item.productImage || null}
                                layoutUrl={item.layoutImage || null}
                                title={item.productName}
                                size={22}
                                className="shrink-0 rounded border border-slate-200"
                              />
                              <span className="font-medium break-words whitespace-normal" title={item.productName}>
                                {item.productName}
                              </span>
                            </div>
                            <span className="font-mono text-[11px] text-indigo-700 shrink-0 ml-2 font-semibold">
                              {formatNumber(item.remainingPlanQty)} {item.unit}
                            </span>
                          </div>
                        ))}
                        {group.items.length > 3 && (
                          <div className="text-[10px] text-slate-400 italic text-right">
                            + {group.items.length - 3} sản phẩm khác...
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Footer of Card */}
                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2 mt-1">
                      <div className="flex items-center gap-1 text-[11px] text-slate-500">
                        <Calendar className="h-3 w-3 text-slate-400" />
                        <span>
                          {group.deliveryDeadline
                            ? new Date(group.deliveryDeadline).toLocaleDateString("vi-VN")
                            : "Không có hạn"}
                        </span>
                      </div>

                      <Button
                        type="button"
                        size="sm"
                        data-testid={`select-prepress-btn-${group.prepressCode || group.id}`}
                        className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-3 shadow-2xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect(group);
                          onOpenChange(false);
                        }}
                      >
                        <Zap className="h-3 w-3 mr-1" /> Chọn Bài Này
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="p-3 sm:p-4 border-t border-slate-200 bg-white flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="text-xs text-slate-500 text-left">
            💡 Mẹo: Nhập bài bình tự động tính toán số con, khổ in phôi và kẽm CTP.
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {onManualEntry && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                  onManualEntry();
                }}
                className="h-8 text-xs text-slate-600"
              >
                Nhập thủ công
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8 text-xs"
            >
              Đóng
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
export default SelectPrepressItemModal;
